import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { logoutUser } from './authService';
import { myFirebaseAuth } from '../../firebase/config';
import { FiledLienCreate, FiledLienUpdate } from '@dill/dill-shared';
import { firebaseFileUploadHelper } from '../../utils/helpers';

export const getSupplierFiledLiens = createAsyncThunk(
  'filedLiens/getSupplierFiledLiens',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLiens/getSupplierFiledLiens`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getFiledLien = createAsyncThunk(
  'releases/getFiledLien',
  async ({ supplierId, filedLienId }: { supplierId: string; filedLienId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLiens/getFiledLien/${filedLienId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateFiledLien = createAsyncThunk(
  'filedLiens/updateFiledLien',
  async (
    {
      supplierId,
      filedLienId,
      filedLienDetails,
    }: {
      supplierId: string;
      filedLienId: string;
      filedLienDetails: FiledLienUpdate;
      isHideLoadingIndicator?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      let filedLienData = {
        ...filedLienDetails,
      };

      if (filedLienDetails.pdf?.url && filedLienId && filedLienDetails?.jobId) {
        const storagePathName = `filedLiens/${filedLienDetails.supplierId ?? ''}/${
          filedLienDetails?.jobId ?? ''
        }/${filedLienId}`;
        const downloadUrl = await firebaseFileUploadHelper({
          blobUrl: filedLienDetails.pdf?.url,
          fileNameWithExtension: `filedLien-${filedLienId}-${new Date().getTime()}.pdf`,
          storagePath: storagePathName,
        });
        filedLienData = {
          ...filedLienData,
          pdf: { url: downloadUrl ?? '', name: filedLienDetails.pdf?.name ?? '' },
        };
      }

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLiens/updateFiledLien/${filedLienId}`,
        {
          ...filedLienData,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createFiledLien = createAsyncThunk(
  'filedLiens/createFiledLien',
  async (
    {
      supplierId,
      filedLienDetails,
    }: {
      supplierId: string;
      filedLienDetails: FiledLienCreate;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const filedLienData = {
        ...filedLienDetails,
      };

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLiens/createFiledLien`,
        { ...filedLienData },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response?.data?.data?.id) {
        const storagePathName = `filedLiens/${filedLienDetails.supplierId ?? ''}/${
          filedLienDetails?.jobId ?? ''
        }/${response.data.data.id}`;
        let filedLienUpdateData = {};

        if (filedLienDetails.pdf?.url) {
          const downloadUrl = await firebaseFileUploadHelper({
            blobUrl: filedLienDetails.pdf?.url,
            fileNameWithExtension: `filedLien-${response.data.data.id}-${new Date().getTime()}.pdf`,
            storagePath: storagePathName,
          });

          filedLienUpdateData = {
            pdf: { url: downloadUrl ?? '', name: filedLienDetails.pdf?.name ?? '' },
          };
          await thunkApi.dispatch(
            updateFiledLien({
              supplierId: supplierId,
              filedLienId: response.data.data.id,
              filedLienDetails: filedLienUpdateData,
            })
          );
        }
      }
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const deleteFiledLien = createAsyncThunk(
  'filedLiens/deleteFiledLien',
  async (
    {
      supplierId,
      filedLienId,
    }: {
      supplierId: string;
      filedLienId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/filedLiens/deleteFiledLien/${filedLienId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
